import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Check, ChevronDown, ClipboardList, Plus } from 'lucide-react';
import { toast } from 'react-toastify';

import type { IConsultation, IJournalResult } from '../../store/types';
import { getTime } from '../../utils/getTime';
import { AiAbstractTab } from '../../pages/journal/ai-abstract-tab';
import { AiCoachSupervisorTab } from '../../pages/journal/ai-coach-supervisor-tab';
import { AiHomeworkTab } from '../../pages/journal/ai-homework-tab';
import { AiMetaphorTab } from '../../pages/journal/ai-metaphor-tab';
import { AiSupervisorTab } from '../../pages/journal/ai-supervisor-tab';
import { AiTalesTherapistTab } from '../../pages/journal/ai-tales-therapist-tab';
import { AiTranscriptionTab } from '../../pages/journal/ai-transcription-tab';
import { AddReportTab } from '../../pages/journal/add-report-tab';
import { JournalMainTab } from '../../pages/journal/journal-main-tab';
import { JournalModalPanel } from '../../pages/journal/journal-modal-panel';
import { buildJournalResultSaveBody, getEmptyJournalResult } from '../../pages/journal/journal-result-save';
import type { Comments } from '../../pages/journal/types';
import { useEditConsultationMutation, useGetConsultationQuery } from '../../services/users.api';
import { Modal, Heading, Pressable, BackButton, TabList } from '../../shared/ui';

import { JournalToolsReport } from './journal-tools-report';

export interface SessionDetailSession {
  id: number;
  date: string;
  rawDate: string;
  datetimeStart: string;
  datetimeEnd: string;
  meetData: IJournalResult | null;
  title: string;
  summary: string | null;
  orderNumber?: number;
}

const truncate = (text: string, maxLen: number) => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}…`;
};

/** Карточка сессии для шапки SessionDetail из данных консультации. */
export function buildSessionDetailSession(consultation: IConsultation): SessionDetailSession {
  const first = consultation.result?.[0];
  const fromConclusion = first?.result?.trim();
  const fromDpdg = first?.dpdg?.comment?.trim();
  const fromQuestion = first?.questions?.[0]?.question?.trim();
  let title = 'Без названия';
  if (fromConclusion) title = truncate(fromConclusion.split('\n')[0] ?? fromConclusion, 70);
  else if (fromDpdg) title = truncate(fromDpdg.split('\n')[0] ?? fromDpdg, 70);
  else if (fromQuestion) title = truncate(fromQuestion.split('\n')[0] ?? fromQuestion, 70);

  const summaryCandidate = fromDpdg || fromConclusion;
  const summary =
    summaryCandidate && summaryCandidate.length > 0
      ? truncate(summaryCandidate.split('\n')[0] ?? summaryCandidate, 160)
      : null;

  const date = `${dayjs(new Date(consultation.date)).format('DD.MM.YYYY')} ${getTime(new Date(consultation.datetime_start))}`;

  return {
    id: consultation.id,
    date,
    rawDate: consultation.date,
    datetimeStart: consultation.datetime_start,
    datetimeEnd: consultation.datetime_end,
    meetData: consultation.result?.[0] ?? null,
    title: title === 'Без названия' ? 'Сессия' : title,
    summary,
    orderNumber: consultation.consultation_number,
  };
}

export interface SessionDetailProps {
  /** Для запросов /consultation-service/.../results/* */
  consultationId: number;
  session: SessionDetailSession;
  onBack: () => void;
  /** Подпись вкладки из `reportTypes`, например «ИИ супервизия» */
  initialTab?: string;
  clientName?: string;
}

const reportTypes = [
  'Инструменты',
  'Транскрипция',
  'ИИ супервизия',
  'Конспект',
  'Домашнее задание',
  'Сказкотерапия',
  'Метафоры',
  'ИИ ICF коуч',
  'SOAP',
  'DAP',
  'BIRP',
  'GIRP',
] as const;

type ReportType = (typeof reportTypes)[number];
type SessionDetailTab = ReportType | 'add';

const makeUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}/${url}`;
};

const getCommentsFromMeetData = (meetData: IJournalResult | null): Comments => ({
  emotions: meetData?.emotions?.map((el) => el.comment) || [],
  mac_cards: meetData?.mac_cards?.map((el) => el.comment) || [],
  wheels: meetData?.wheel?.map((el) => el.comment) || [],
  color_code: meetData?.color_code?.map((el) => el.comment) || [],
  dpdg: meetData?.dpdg ? meetData.dpdg.comment : '',
  clouds: meetData?.clouds?.map((el) => el.comment) || [],
  sand_therapy: meetData?.sand_therapy?.map((el) => el.comment) || [],
  result: meetData?.result || '',
  mac_cards_by_participant: meetData?.mac_cards_by_participant
    ? Object.fromEntries(
        Object.entries(meetData.mac_cards_by_participant).map(([id, entries]) => [
          id,
          entries.map((e) => e.comment ?? ''),
        ]),
      )
    : {},
  emotions_by_participant: meetData?.emotions_by_participant
    ? Object.fromEntries(
        Object.entries(meetData.emotions_by_participant).map(([id, entries]) => [
          id,
          entries.map((e) => e.comment ?? ''),
        ]),
      )
    : {},
  session_conclusion_by_participant: meetData?.session_conclusion_by_participant ?? {},
});

function ReportContent({
  activeReport,
  consultationId,
  meetData,
  commentsData,
  setCommentsData,
  isGroupConsultation,
  onSave,
  onOpenConclusionTips,
}: {
  activeReport: SessionDetailTab;
  consultationId: number;
  meetData: IJournalResult | null;
  commentsData: Comments;
  setCommentsData: React.Dispatch<React.SetStateAction<Comments>>;
  isGroupConsultation: boolean;
  onSave: () => void;
  onOpenConclusionTips: () => void;
}) {
  const journalMainTab = (
    <JournalMainTab
      meetData={meetData}
      commentsData={commentsData}
      setCommentsData={setCommentsData}
      onSave={onSave}
      onOpenConclusionTips={onOpenConclusionTips}
      makeUrl={makeUrl}
      consultationId={consultationId}
      isGroupTherapy={isGroupConsultation}
    />
  );

  switch (activeReport) {
    case 'Транскрипция':
      return <AiTranscriptionTab consultationId={consultationId} bypassFeatureFlag />;
    case 'Инструменты':
      // Отправка задания адресована одному клиенту, поэтому в групповом журнале её нет.
      if (isGroupConsultation) return journalMainTab;

      return <JournalToolsReport consultationId={consultationId} meetData={meetData} journalMainTab={journalMainTab} />;
    case 'ИИ супервизия':
      return <AiSupervisorTab consultationId={consultationId} />;
    case 'Конспект':
      return <AiAbstractTab consultationId={consultationId} />;
    case 'Домашнее задание':
      return <AiHomeworkTab consultationId={consultationId} />;
    case 'Сказкотерапия':
      return <AiTalesTherapistTab consultationId={consultationId} />;
    case 'Метафоры':
      return <AiMetaphorTab consultationId={consultationId} />;
    case 'ИИ ICF коуч':
      return <AiCoachSupervisorTab consultationId={consultationId} />;
    case 'SOAP':
    case 'DAP':
    case 'BIRP':
    case 'GIRP':
      return (
        <div className="py-8 text-center">
          <p className="text-ink text-heading-md">Формат «{activeReport}» скоро будет доступен.</p>
        </div>
      );
    case 'add':
      return <AddReportTab />;
    default:
      return null;
  }
}

function normalizeInitialTab(tab: string | undefined): SessionDetailTab {
  if (!tab) return 'Инструменты';
  if (tab === 'add') return 'add';
  if (tab === 'Транскрипт') return 'Транскрипция';
  const found = reportTypes.find((t) => t === tab);
  return found ?? 'Инструменты';
}

export function SessionDetail({ consultationId, session, onBack, initialTab, clientName }: SessionDetailProps) {
  const { data: consultation } = useGetConsultationQuery(String(consultationId), { skip: !consultationId });
  const [editConsultation] = useEditConsultationMutation();
  const resolvedSession = useMemo(
    () => (consultation ? buildSessionDetailSession(consultation) : session),
    [consultation, session],
  );
  const resolvedMeetData = consultation?.result?.[0] ?? session.meetData;
  const baseMeetData = resolvedMeetData ?? getEmptyJournalResult();
  const resolvedClientName = clientName || consultation?.client_full_name || resolvedSession.title;
  const isGroupConsultation = Boolean(consultation?.is_group);
  const [activeReport, setActiveReport] = useState<SessionDetailTab>(() => normalizeInitialTab(initialTab));
  const [modalConclusion, setModalConclusion] = useState(false);
  const [isReportPickerOpen, setIsReportPickerOpen] = useState(false);
  const [commentsData, setCommentsData] = useState<Comments>(() => getCommentsFromMeetData(session.meetData));
  const reportPickerButtonRef = useRef<HTMLButtonElement>(null);
  const reportPickerDialogRef = useRef<HTMLDivElement>(null);

  const handleSave = useCallback(() => {
    if (!consultation) return;

    editConsultation({
      id: consultation.id,
      body: buildJournalResultSaveBody(baseMeetData, commentsData),
    })
      .unwrap()
      .then(() => {
        toast.success('Данные успешно сохранены');
      })
      .catch(() => {
        toast.error('Произошла ошибка');
      });
  }, [baseMeetData, commentsData, consultation, editConsultation]);

  const conclusionTips = useMemo(
    () => (
      <>
        <ul className="list-decimal text-ui lg:text-body-xl text-ink">
          <li>{isGroupConsultation ? 'Тип встречи' : 'Тип клиента'}</li>
          <li>Наличие психиатрического диагноза</li>
          <li>Фармакотерапия (какие препараты)</li>
          <li>Жалоба \ проблема \ запрос</li>
          <li>Рабочая гипотеза</li>
          <li>{isGroupConsultation ? 'Протективные факторы встречи' : 'Протективные факторы Клиента'}</li>
          <li>{isGroupConsultation ? 'Установки встречи (если есть)' : 'Установки Клиента (если есть)'}</li>
          <li>Методы, которые использовались на сессии</li>
          <li>
            Техники и интервенции, которые использовались (например: сократический диалог, модуляция установок,
            дерефлексия, парадоксальная интенция, самораскрытие, отражение, конфронтация, прямое руководство, поддержка,
            информирование, библиотерапия, метод «Горячего стула» и т.п.)
          </li>
          <li>План дальнейшей работы</li>
        </ul>
      </>
    ),
    [isGroupConsultation],
  );
  const periodLabel = useMemo(() => {
    const start = new Date(resolvedSession.datetimeStart);
    const end = new Date(resolvedSession.datetimeEnd);
    const dateLabel = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(start);
    return `${dateLabel}, ${getTime(start)} - ${getTime(end)}`;
  }, [resolvedSession.datetimeEnd, resolvedSession.datetimeStart]);

  useEffect(() => {
    setActiveReport(normalizeInitialTab(initialTab));
  }, [initialTab, consultationId]);

  useEffect(() => {
    setCommentsData(getCommentsFromMeetData(resolvedMeetData));
  }, [resolvedMeetData]);

  const closeReportPicker = useCallback(() => {
    setIsReportPickerOpen(false);
    window.requestAnimationFrame(() => reportPickerButtonRef.current?.focus());
  }, []);

  const selectReport = (report: SessionDetailTab) => {
    setActiveReport(report);
    closeReportPicker();
  };

  useEffect(() => {
    if (!isReportPickerOpen) return;

    const frame = window.requestAnimationFrame(() => {
      reportPickerDialogRef.current?.querySelector<HTMLButtonElement>('[aria-current="true"]')?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isReportPickerOpen]);

  return (
    <div className="min-w-0">
      <div className="mb-4 min-w-0">
        <BackButton label="Назад к сессиям" showLabel className="mb-3 md:mb-4" onClick={onBack} />
        <Heading size="lg" as="h1" className="text-pretty md:hidden">
          {resolvedSession.orderNumber ? `Сессия №${resolvedSession.orderNumber}` : 'Сессия'}
        </Heading>
        <Heading size="xl" as="h2" className="hidden tabular-nums md:block">
          {periodLabel}
        </Heading>
      </div>

      <div className="mb-5 min-w-0 rounded-control border border-line bg-white p-4 md:mb-6 md:border-0 md:bg-transparent md:p-0">
        <p className="mb-1 text-ui tabular-nums text-ink-muted md:hidden">{periodLabel}</p>
        <span className="text-ui text-ink-muted lg:text-body-xl">Клиент: </span>
        <span className="break-words text-ui-strong text-ink lg:text-body-xl-strong">{resolvedClientName}</span>
      </div>

      <Pressable
        ref={reportPickerButtonRef}
        aria-expanded={isReportPickerOpen}
        aria-controls="journal-report-picker"
        onClick={() => setIsReportPickerOpen(true)}
        className="mb-4 flex min-h-12 w-full touch-manipulation items-center justify-between gap-3 rounded-control border border-line-strong bg-white px-4 py-3 text-left font-medium text-ink shadow-raised transition-colors hover:border-primary md:hidden"
      >
        <span className="min-w-0 truncate">Раздел: {activeReport === 'add' ? 'Добавить отчёт' : activeReport}</span>
        <ChevronDown size={20} className="shrink-0 text-ink-subtle" aria-hidden />
      </Pressable>

      <div className="mb-4 hidden md:block">
        <TabList<SessionDetailTab>
          variant="folder"
          value={activeReport}
          onChange={setActiveReport}
          items={[
            ...reportTypes.map((type) => ({
              id: type,
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <ClipboardList size={12} aria-hidden />
                  {type}
                </span>
              ),
            })),
            {
              id: 'add' as const,
              detached: true,
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <Plus size={12} aria-hidden />
                  Добавить
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* overflow-x-clip, а не -hidden: hidden форсирует overflow-y:auto и ломает sticky в ClientSendPanel */}
      <div className="min-w-0 overflow-x-clip rounded-control border border-line bg-white p-4 md:p-6">
        <ReportContent
          activeReport={activeReport}
          consultationId={consultationId}
          meetData={resolvedMeetData}
          commentsData={commentsData}
          setCommentsData={setCommentsData}
          isGroupConsultation={isGroupConsultation}
          onSave={handleSave}
          onOpenConclusionTips={() => setModalConclusion(true)}
        />
      </div>

      <JournalModalPanel
        isShow={modalConclusion}
        title="Возможная структура заключения"
        onClick={() => setModalConclusion(false)}
      >
        {conclusionTips}
      </JournalModalPanel>

      <Modal
        isOpen={isReportPickerOpen}
        title="Выберите раздел"
        sheetOnMobile
        closeOnBackdrop
        onClose={closeReportPicker}
      >
        <div ref={reportPickerDialogRef} id="journal-report-picker" className="space-y-1">
          {[...reportTypes, 'add' as const].map((type) => {
            const isActive = activeReport === type;
            const label = type === 'add' ? 'Добавить отчёт' : type;
            return (
              <Pressable
                key={type}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => selectReport(type)}
                className={`flex min-h-12 w-full touch-manipulation items-center gap-3 rounded-control px-3 py-2.5 text-left text-ui-strong transition-colors ${
                  isActive ? 'bg-danger-surface text-primary' : 'text-ink hover:bg-surface-muted'
                }`}
              >
                {type === 'add' ? <Plus size={19} aria-hidden /> : <ClipboardList size={19} aria-hidden />}
                <span className="min-w-0 flex-1 break-words">{label}</span>
                {isActive && <Check size={20} className="shrink-0" aria-hidden />}
              </Pressable>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
